import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, Coins, ShoppingCart, Gift, Loader2, X,
  ChevronLeft, ChevronRight, Zap, TrendingUp,
} from 'lucide-react';
import { api } from '../../utils/api';
import { useAuthStore } from '../../stores/auth';
import { formatDate } from '../../utils/formatters';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface CreditBalance {
  balance: number;
  totalPurchased: number;
  totalUsed: number;
  totalBonused: number;
  pricing: FeaturePricing[];
}

interface CreditTransaction {
  id: string;
  type: 'PURCHASE' | 'USAGE' | 'BONUS' | 'REFUND' | 'ADJUSTMENT';
  amount: number;
  balanceAfter: number;
  description: string;
  feature: string | null;
  createdAt: string;
}

interface FeatureUsage {
  feature: string;
  _sum: { creditsUsed: number };
  _count: number;
}

interface UsageData {
  month: string;
  features: FeatureUsage[];
  totalUsed: number;
  totalCalls: number;
}

interface FeaturePricing {
  feature: string;
  displayName: string;
  creditsPerUse: number;
  description: string;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const TYPE_STYLES: Record<string, { label: string; bg: string; text: string }> = {
  PURCHASE:   { label: 'Purchase',   bg: 'bg-blue-500/10',   text: 'text-blue-400' },
  USAGE:      { label: 'Usage',      bg: 'bg-orange-500/10', text: 'text-orange-400' },
  BONUS:      { label: 'Bonus',      bg: 'bg-green-500/10',  text: 'text-green-400' },
  REFUND:     { label: 'Refund',     bg: 'bg-purple-500/10', text: 'text-purple-400' },
  ADJUSTMENT: { label: 'Adjustment', bg: 'bg-gray-500/10',   text: 'text-gray-400' },
};

const PAGE_SIZE = 15;

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function CreditsPage() {
  const { user } = useAuthStore();
  const isAdminOrOwner = user?.role?.name === 'admin';

  // Data states
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [pricing, setPricing] = useState<FeaturePricing[]>([]);
  const [plans, setPlans] = useState<any[]>([]);

  // UI states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [txPage, setTxPage] = useState(1);
  const [txTotal, setTxTotal] = useState(0);
  const [showPurchase, setShowPurchase] = useState(false);

  const totalTxPages = Math.max(1, Math.ceil(txTotal / PAGE_SIZE));

  /* ---- Data fetching ---- */

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [balRes, txRes, usageRes, pricingRes, plansRes] = await Promise.all([
        api.get('/credits/balance').catch(() => null),
        api.get('/credits/transactions', { params: { offset: 0, limit: PAGE_SIZE } }).catch(() => null),
        api.get('/credits/usage').catch(() => null),
        api.get('/credits/pricing').catch(() => null),
        api.get('/credits/plans').catch(() => null),
      ]);

      if (balRes?.data?.data) setBalance(balRes.data.data);
      if (txRes?.data?.data) {
        setTransactions(txRes.data.data);
        setTxTotal(txRes.data.meta?.total ?? txRes.data.data.length);
      }
      if (usageRes?.data?.data) setUsage(usageRes.data.data);
      if (pricingRes?.data?.data) setPricing(pricingRes.data.data);
      else if (balRes?.data?.data?.pricing) setPricing(balRes.data.data.pricing);
      if (plansRes?.data?.data) setPlans(plansRes.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load credit data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTransactions = useCallback(async (page: number) => {
    try {
      const res = await api.get('/credits/transactions', {
        params: { offset: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE },
      });
      if (res.data?.data) {
        setTransactions(res.data.data);
        setTxTotal(res.data.meta?.total ?? res.data.data.length);
        setTxPage(page);
      }
    } catch {}
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  /* ---- Usage chart helpers ---- */

  const maxUsage = usage?.features
    ? Math.max(...usage.features.map((f) => f._sum.creditsUsed), 1)
    : 1;

  const CHART_COLORS = [
    'bg-blue-500', 'bg-indigo-500', 'bg-purple-500', 'bg-pink-500',
    'bg-orange-500', 'bg-teal-500', 'bg-cyan-500', 'bg-emerald-500',
  ];

  /* ---- Loading / Error ---- */

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
        <span className="ml-2 text-sm text-[var(--text-muted)]">Loading credits...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-6 text-center">
        <p className="text-sm text-red-400">{error}</p>
        <button onClick={fetchAll} className="mt-2 text-sm text-[var(--saffron-600)] hover:underline">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500">
            <Sparkles size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[var(--text-primary)]">AI Credits</h1>
            <p className="text-sm text-[var(--text-muted)]">
              Manage your AI credit balance and usage
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border border-blue-500/20 px-4 py-2 text-sm font-semibold text-blue-400">
            <Sparkles size={14} />
            {(balance?.balance ?? 0).toLocaleString('en-IN')} credits
          </span>
          {isAdminOrOwner && (
            <button
              onClick={() => setShowPurchase(true)}
              className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:from-blue-700 hover:to-indigo-700 transition-all"
            >
              <ShoppingCart size={16} />
              Buy Credits
            </button>
          )}
        </div>
      </div>

      {/* Stat Cards */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Available Balance"
          value={balance?.balance ?? 0}
          icon={Coins}
          color="text-blue-400"
          bg="bg-blue-500/10"
        />
        <StatCard
          label="Total Purchased"
          value={balance?.totalPurchased ?? 0}
          icon={ShoppingCart}
          color="text-green-400"
          bg="bg-green-500/10"
        />
        <StatCard
          label="Total Used"
          value={balance?.totalUsed ?? 0}
          icon={Zap}
          color="text-orange-400"
          bg="bg-orange-500/10"
        />
        <StatCard
          label="Bonus Credits"
          value={balance?.totalBonused ?? 0}
          icon={Gift}
          color="text-purple-400"
          bg="bg-purple-500/10"
        />
      </div>

      {/* Credit Plans / Allocation */}
      {plans.length > 0 && (
        <div className="mb-6 rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <div className="mb-4 flex items-center gap-2">
            <Sparkles size={18} className="text-[var(--text-muted)]" />
            <h2 className="text-lg font-semibold text-[var(--text-primary)]">
              Credit Plans & Allocation
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {plans.map((plan) => (
              <div key={plan.name} className="rounded-lg border border-[var(--border-secondary)] bg-[var(--bg-inset)] p-4">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="font-medium text-[var(--text-primary)]">{plan.name}</h3>
                  <span className="rounded-full bg-blue-500/10 px-2 py-0.5 text-xs font-semibold text-blue-400">
                    {plan.credits?.toLocaleString() || 0} CR
                  </span>
                </div>
                <div className="space-y-1 text-xs text-[var(--text-muted)]">
                  {plan.features && plan.features.map((f: string) => (
                    <div key={f} className="flex items-center gap-1">
                      <Sparkles size={10} className="text-blue-400" />
                      {f.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </div>
                  ))}
                </div>
                {plan.price && (
                  <div className="mt-3 border-t border-[var(--border-secondary)] pt-2">
                    <span className="text-sm font-semibold text-[var(--text-primary)]">
                      ₹{plan.price.toLocaleString()}
                    </span>
                    <span className="text-xs text-[var(--text-muted)]"> / month</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Usage Chart + Pricing Table grid */}
      <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Usage Chart */}
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <div className="mb-4 flex items-center gap-2">
            <TrendingUp size={18} className="text-[var(--text-muted)]" />
            <h2 className="text-lg font-semibold text-[var(--text-primary)]">
              Usage This Month
            </h2>
          </div>
          {usage?.features && usage.features.length > 0 ? (
            <div className="space-y-3">
              {usage.features.map((f, idx) => {
                const pct = Math.round((f._sum.creditsUsed / maxUsage) * 100);
                const colorClass = CHART_COLORS[idx % CHART_COLORS.length];
                return (
                  <div key={f.feature}>
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-sm text-[var(--text-secondary)]">
                        {f.feature.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                      </span>
                      <span className="text-sm font-medium text-[var(--text-primary)]">
                        {f._sum.creditsUsed.toLocaleString('en-IN')} credits
                        <span className="ml-1 text-xs text-[var(--text-muted)]">
                          ({f._count} calls)
                        </span>
                      </span>
                    </div>
                    <div className="h-3 w-full overflow-hidden rounded-full bg-[var(--bg-inset)]">
                      <div
                        className={`h-full rounded-full ${colorClass} transition-all duration-500`}
                        style={{ width: `${Math.max(pct, 2)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
              <div className="mt-4 flex items-center justify-between border-t border-[var(--border-secondary)] pt-3">
                <span className="text-sm text-[var(--text-muted)]">Total this month</span>
                <span className="text-sm font-semibold text-[var(--text-primary)]">
                  {(usage.totalUsed ?? 0).toLocaleString('en-IN')} credits / {(usage.totalCalls ?? 0).toLocaleString('en-IN')} calls
                </span>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-[var(--text-muted)]">
              <Zap size={32} className="mb-2 opacity-50" />
              <p className="text-sm">No usage this month</p>
              <p className="text-xs">Start using AI features to see usage data here</p>
            </div>
          )}
        </div>

        {/* Feature Pricing Table */}
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <div className="mb-4 flex items-center gap-2">
            <Coins size={18} className="text-[var(--text-muted)]" />
            <h2 className="text-lg font-semibold text-[var(--text-primary)]">
              Feature Pricing
            </h2>
          </div>
          {pricing.length > 0 ? (
            <div className="overflow-hidden rounded-lg border border-[var(--border-secondary)]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--border-secondary)] bg-[var(--bg-inset)]">
                    <th className="px-4 py-2.5 text-left font-medium text-[var(--text-muted)]">Feature</th>
                    <th className="px-4 py-2.5 text-right font-medium text-[var(--text-muted)]">Credits</th>
                  </tr>
                </thead>
                <tbody>
                  {pricing.map((p) => (
                    <tr key={p.feature} className="border-b border-[var(--border-secondary)] last:border-b-0">
                      <td className="px-4 py-3">
                        <p className="font-medium text-[var(--text-primary)]">{p.displayName}</p>
                        <p className="text-xs text-[var(--text-muted)]">{p.description}</p>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="inline-flex items-center gap-1 rounded-full bg-blue-500/10 px-2.5 py-0.5 text-xs font-semibold text-blue-400">
                          <Sparkles size={10} />
                          {p.creditsPerUse}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-[var(--text-muted)]">
              <Coins size={32} className="mb-2 opacity-50" />
              <p className="text-sm">No pricing data available</p>
            </div>
          )}
        </div>
      </div>

      {/* Transaction History */}
      <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">
            Transaction History
          </h2>
          {txTotal > 0 && (
            <span className="text-xs text-[var(--text-muted)]">
              {txTotal} total transactions
            </span>
          )}
        </div>

        {transactions.length > 0 ? (
          <>
            <div className="overflow-hidden rounded-lg border border-[var(--border-secondary)]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--border-secondary)] bg-[var(--bg-inset)]">
                    <th className="px-4 py-2.5 text-left font-medium text-[var(--text-muted)]">Type</th>
                    <th className="px-4 py-2.5 text-left font-medium text-[var(--text-muted)]">Description</th>
                    <th className="px-4 py-2.5 text-right font-medium text-[var(--text-muted)]">Amount</th>
                    <th className="px-4 py-2.5 text-right font-medium text-[var(--text-muted)]">Balance</th>
                    <th className="px-4 py-2.5 text-right font-medium text-[var(--text-muted)]">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx) => {
                    const style = TYPE_STYLES[tx.type] ?? TYPE_STYLES.ADJUSTMENT!;
                    const isPositive = tx.amount > 0;
                    return (
                      <tr
                        key={tx.id}
                        className="border-b border-[var(--border-secondary)] last:border-b-0 hover:bg-[var(--bg-surface-hover)] transition-colors"
                      >
                        <td className="px-4 py-3">
                          <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${style.bg} ${style.text}`}>
                            {style.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-[var(--text-secondary)]">
                          {tx.description}
                          {tx.feature && (
                            <span className="ml-1 text-xs text-[var(--text-muted)]">
                              ({tx.feature.replace(/_/g, ' ')})
                            </span>
                          )}
                        </td>
                        <td className={`px-4 py-3 text-right font-semibold ${isPositive ? 'text-green-400' : 'text-orange-400'}`}>
                          {isPositive ? '+' : ''}{tx.amount.toLocaleString('en-IN')}
                        </td>
                        <td className="px-4 py-3 text-right text-[var(--text-muted)]">
                          {tx.balanceAfter.toLocaleString('en-IN')}
                        </td>
                        <td className="px-4 py-3 text-right text-[var(--text-muted)]">
                          {formatDate(tx.createdAt)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalTxPages > 1 && (
              <div className="mt-4 flex items-center justify-between">
                <span className="text-xs text-[var(--text-muted)]">
                  Page {txPage} of {totalTxPages}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => fetchTransactions(txPage - 1)}
                    disabled={txPage <= 1}
                    className="flex items-center gap-1 rounded-lg border border-[var(--border-primary)] px-3 py-1.5 text-xs text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={14} /> Previous
                  </button>
                  <button
                    onClick={() => fetchTransactions(txPage + 1)}
                    disabled={txPage >= totalTxPages}
                    className="flex items-center gap-1 rounded-lg border border-[var(--border-primary)] px-3 py-1.5 text-xs text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Next <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-[var(--text-muted)]">
            <Coins size={32} className="mb-2 opacity-50" />
            <p className="text-sm">No transactions yet</p>
          </div>
        )}
      </div>

      {/* Purchase Modal */}
      {showPurchase && (
        <PurchaseModal
          onClose={() => setShowPurchase(false)}
          onSuccess={() => {
            setShowPurchase(false);
            fetchAll();
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  StatCard                                                           */
/* ------------------------------------------------------------------ */

function StatCard({
  label,
  value,
  icon: Icon,
  color,
  bg,
}: {
  label: string;
  value: number;
  icon: typeof Coins;
  color: string;
  bg: string;
}) {
  return (
    <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-5">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${bg}`}>
          <Icon size={18} className={color} />
        </div>
        <div>
          <p className="text-xs text-[var(--text-muted)]">{label}</p>
          <p className="text-xl font-bold text-[var(--text-primary)]">
            {value.toLocaleString('en-IN')}
          </p>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  PurchaseModal                                                      */
/* ------------------------------------------------------------------ */

function PurchaseModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [credits, setCredits] = useState<number>(5000); // Standard pack (popular default)
  const [paymentRef, setPaymentRef] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'select' | 'payment' | 'verify'>('select');

  // Per Ardhanex Pricing Strategy (Apr 7 2026) — ₹0.50/credit retail, with
  // volume discounts: 0% / 10% / 20% / 30% off on 1k / 5k / 25k / 100k packs.
  // All packs keep ≥80% margin even after discounts.
  const CREDIT_PACKS = [
    { credits: 1000,   price: 500,   label: 'Mini',     popular: false },
    { credits: 5000,   price: 2250,  label: 'Standard', popular: true  },
    { credits: 25000,  price: 10000, label: 'Pro',      popular: false },
    { credits: 100000, price: 35000, label: 'Scale',    popular: false },
  ];

  const selectedPack = CREDIT_PACKS.find((p) => p.credits === credits) || CREDIT_PACKS[0];

  const handleVerifyAndPurchase = async () => {
    if (!paymentRef.trim()) {
      setError('Please enter the payment transaction ID after completing payment');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      await api.post('/credits/purchase', {
        credits,
        paymentRef: paymentRef.trim(),
      });
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Purchase verification failed. Please contact support.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 pb-4 border-b border-[var(--border-primary)] sticky top-0 z-10 bg-[var(--bg-surface)] rounded-t-xl">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500">
              <ShoppingCart size={16} className="text-white" />
            </div>
            <h3 className="text-lg font-semibold text-[var(--text-primary)]">
              {step === 'select' ? 'Choose Credit Pack' : step === 'payment' ? 'Complete Payment' : 'Verify Payment'}
            </h3>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 text-[var(--text-muted)] hover:bg-[var(--bg-surface-hover)] transition-colors">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 pt-4 space-y-4">

          {/* Step 1: Select Pack */}
          {step === 'select' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                {CREDIT_PACKS.map((pack) => (
                  <button
                    key={pack.credits}
                    onClick={() => setCredits(pack.credits)}
                    className={`relative rounded-xl border p-4 text-left transition-all ${
                      credits === pack.credits
                        ? 'border-blue-500 bg-blue-500/10 ring-1 ring-blue-500/30'
                        : 'border-[var(--border-primary)] hover:border-[var(--border-secondary)] hover:bg-[var(--bg-inset)]'
                    }`}
                  >
                    {pack.popular && (
                      <span className="absolute -top-2 right-3 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 px-2 py-0.5 text-[10px] font-bold text-white">POPULAR</span>
                    )}
                    <p className="text-xs font-medium text-[var(--text-muted)]">{pack.label}</p>
                    <p className="mt-1 flex items-center gap-1 text-xl font-bold text-[var(--text-primary)]">
                      <Sparkles size={14} className="text-blue-400" />
                      {pack.credits.toLocaleString('en-IN')}
                    </p>
                    <p className="mt-1 text-sm font-semibold text-green-400">
                      ₹{pack.price.toLocaleString('en-IN')}
                    </p>
                    <p className="text-[10px] text-[var(--text-muted)]">
                      ₹{(pack.price / pack.credits).toFixed(2)} per credit
                    </p>
                  </button>
                ))}
              </div>
              <button
                onClick={() => setStep('payment')}
                className="w-full flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:from-blue-700 hover:to-indigo-700 transition-all"
              >
                Continue to Payment — ₹{selectedPack.price.toLocaleString('en-IN')}
              </button>
            </>
          )}

          {/* Step 2: Payment Instructions */}
          {step === 'payment' && (
            <>
              <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-4 space-y-3">
                <p className="text-sm font-semibold text-[var(--text-primary)]">Order Summary</p>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[var(--text-secondary)]">{selectedPack.label} Pack — {credits.toLocaleString('en-IN')} credits</span>
                  <span className="font-bold text-[var(--text-primary)]">₹{selectedPack.price.toLocaleString('en-IN')}</span>
                </div>
              </div>

              <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-inset)] p-4 space-y-3">
                <p className="text-sm font-semibold text-[var(--text-primary)]">Pay via UPI / Bank Transfer</p>
                <div className="space-y-2 text-sm text-[var(--text-secondary)]">
                  <div className="flex items-center justify-between rounded-lg bg-[var(--bg-surface)] px-3 py-2 border border-[var(--border-secondary)]">
                    <span className="text-xs text-[var(--text-muted)]">UPI ID</span>
                    <span className="font-mono font-medium text-[var(--text-primary)]">pay@ardhanex.com</span>
                  </div>
                  <div className="flex items-center justify-between rounded-lg bg-[var(--bg-surface)] px-3 py-2 border border-[var(--border-secondary)]">
                    <span className="text-xs text-[var(--text-muted)]">Amount</span>
                    <span className="font-bold text-green-400">₹{selectedPack.price.toLocaleString('en-IN')}</span>
                  </div>
                </div>
                <p className="text-[11px] text-[var(--text-muted)]">
                  After payment, click below to enter your transaction ID for verification.
                </p>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setStep('select')}
                  className="flex-1 rounded-lg border border-[var(--border-primary)] px-4 py-2 text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)] transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={() => setStep('verify')}
                  className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 px-4 py-2 text-sm font-medium text-white hover:from-green-700 hover:to-emerald-700 transition-all"
                >
                  I've Paid — Verify
                </button>
              </div>
            </>
          )}

          {/* Step 3: Verify Payment */}
          {step === 'verify' && (
            <>
              <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-inset)] p-4 space-y-3">
                <p className="text-sm font-semibold text-[var(--text-primary)]">Enter Payment Details</p>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-[var(--text-muted)]">
                    Transaction / UTR Number *
                  </label>
                  <input
                    type="text"
                    value={paymentRef}
                    onChange={(e) => setPaymentRef(e.target.value)}
                    className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2.5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
                    placeholder="e.g. 423156789012 or PhonePe/GPay txn ID"
                  />
                </div>
                <div className="flex items-center justify-between text-xs text-[var(--text-muted)]">
                  <span>{selectedPack.label} Pack — {credits.toLocaleString('en-IN')} credits</span>
                  <span className="font-bold text-green-400">₹{selectedPack.price.toLocaleString('en-IN')}</span>
                </div>
              </div>

              {error && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-400">
                  {error}
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={() => { setStep('payment'); setError(null); }}
                  className="flex-1 rounded-lg border border-[var(--border-primary)] px-4 py-2 text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)] transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleVerifyAndPurchase}
                  disabled={submitting || !paymentRef.trim()}
                  className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {submitting ? (
                    <><Loader2 size={14} className="animate-spin" /> Verifying...</>
                  ) : (
                    <><Sparkles size={14} /> Confirm & Add Credits</>
                  )}
                </button>
              </div>

              <p className="text-[10px] text-center text-[var(--text-muted)]">
                Credits will be added after payment verification. If auto-verification fails, our team will review within 24 hours.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
